import type { Editor } from '@tiptap/core'

/**
 * Notion 互換のトグル（折りたたみ）ブロックを挿入する。
 *
 * - 選択していたブロックのテキストは「タイトル（detailsSummary）」に入れる
 * - 中身（detailsContent）は空の段落で初期化し、作成直後は開いておく
 * - リスト項目内で実行した場合は、いったんリストを解除してから変換する
 *   （バレット/番号とトグルの矢印が二重に表示されるのを防ぐ）
 */
export function insertToggle(editor: Editor): boolean {
  // リスト内ならまずリストを解除してフラットな段落に戻す
  if (editor.isActive('bulletList')) {
    editor.chain().focus().toggleBulletList().run()
  } else if (editor.isActive('orderedList')) {
    editor.chain().focus().toggleOrderedList().run()
  } else if (editor.isActive('taskList')) {
    editor.chain().focus().toggleTaskList().run()
  }

  const { state } = editor
  const { $from, $to } = state.selection
  const range = $from.blockRange($to)
  if (!range) {
    return false
  }

  // 選択ブロックのテキスト（複数ブロックはスペース区切り）をタイトルに使う
  const title = state.doc.textBetween(range.start, range.end, ' ', ' ').trim()
  const summaryContent = title ? [{ type: 'text', text: title }] : []
  const insertPos = range.start

  const inserted = editor
    .chain()
    .focus()
    .insertContentAt(
      { from: range.start, to: range.end },
      {
        type: 'details',
        attrs: { open: true },
        content: [
          { type: 'detailsSummary', content: summaryContent },
          { type: 'detailsContent', content: [{ type: 'paragraph' }] },
        ],
      },
    )
    .run()

  if (!inserted) {
    return false
  }

  // 挿入した details の summary 末尾にカーソルを置く（タイトルに直接入力できるようにする）
  const { doc } = editor.state
  let summaryEnd = -1
  doc.nodesBetween(insertPos, Math.min(doc.content.size, insertPos + 4), (node, pos) => {
    if (summaryEnd === -1 && node.type.name === 'detailsSummary') {
      summaryEnd = pos + node.nodeSize - 1
    }
  })
  if (summaryEnd >= 0) {
    editor.commands.setTextSelection(summaryEnd)
  }

  return true
}
